import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, BuildingOfficeIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useVendor } from '../../contexts/VendorContext';
import apiService from '../../services/api';

const VendorLogin: React.FC = () => {
  const [formData, setFormData] = useState({
    vendorId: '',
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingVendor, setIsLoadingVendor] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);

  const { login } = useAuth();
  const { setCurrentVendor } = useVendor();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleVendorIdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.vendorId) {
      setError('يرجى إدخال معرف البائع');
      return;
    }

    setIsLoadingVendor(true);
    setError('');

    try {
      // Get vendor info
      const response = await apiService.getPublicVendors(1, 100);
      const vendor = response.data?.find((v: any) =>
        v.vendorId === formData.vendorId ||
        v.vendorId.toLowerCase() === formData.vendorId.toLowerCase()
      );

      if (!vendor) {
        setError('البائع غير موجود أو غير نشط');
        return;
      }

      if (!vendor.isActive) {
        setError('البائع غير نشط حالياً');
        return;
      }

      setSelectedVendor(vendor);
      setCurrentVendor(vendor);
      // Also save to localStorage directly to ensure it's available
      localStorage.setItem('currentVendor', JSON.stringify(vendor));
    } catch (err: any) {
      // Extract error message from API response
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('خطأ في التحقق من البائع');
      }
    } finally {
      setIsLoadingVendor(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      setError('يرجى إدخال اسم المستخدم وكلمة المرور');
      return;
    }

    if (!selectedVendor) {
      setError('يرجى إدخال معرف البائع أولاً');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Use vendor login instead of admin login
      const response = await apiService.vendorLogin(formData);

      // Handle the login response manually since we're not using useAuth
      if (response.success && response.token && response.user) {
        localStorage.setItem('token', response.token);

        // Create a simplified user object that matches our User interface
        // Map 'admin' role to 'manager' for vendor admins
        const mappedRole = response.user.role === 'admin' ? 'manager' : response.user.role;
        const userData = {
          _id: response.user.id,
          username: response.user.username,
          fullName: response.user.fullName,
          role: mappedRole,
          branch: {
            _id: response.user.branch.id,
            name: response.user.branch.name,
            code: response.user.branch.code,
            address: '',
            phone: '',
            manager: '',
            isActive: true,
            settings: {
              defaultPrices: {},
              workingHours: {}
            },
            createdAt: new Date()
          },
          permissions: response.user.permissions || [],
          phone: '',
          email: '',
          isActive: true,
          lastLogin: new Date(response.user.lastLogin),
          settings: {
            language: (response.user.settings?.language === 'en' ? 'en' : 'ar') as 'ar' | 'en',
            theme: (response.user.settings?.theme === 'dark' ? 'dark' : 'light') as 'light' | 'dark',
            notifications: response.user.settings?.notifications || {
              email: true,
              sms: false,
              whatsapp: true
            }
          },
          createdAt: new Date(),
          updatedAt: new Date()
        };

        localStorage.setItem('user', JSON.stringify(userData));

        // Ensure vendor info is still set after login
        if (selectedVendor) {
          localStorage.setItem('currentVendor', JSON.stringify(selectedVendor));
        }

        // Force page reload to update AuthContext
        window.location.href = '/vendor';
      } else {
        throw new Error('Invalid login response');
      }
    } catch (err: any) {
      setError(err.message || 'خطأ في تسجيل الدخول');
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToMainLogin = () => {
    navigate('/login');
  };

  const resetVendorSelection = () => {
    setSelectedVendor(null);
    setCurrentVendor(null);
    setFormData(prev => ({ ...prev, vendorId: '' }));
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-parchment-50 rtl px-4 py-12">
      <div className="max-w-md w-full space-y-6">
        {/* Back to home */}
        <Link
          to="/landing"
          className="inline-flex items-center gap-2 text-sm font-medium text-navy-500 transition-colors hover:text-copper-600"
        >
          <ArrowRightIcon className="h-4 w-4" />
          الرئيسية
        </Link>

        {/* Logo and header */}
        <div className="text-center">
          <div className="mx-auto h-20 w-20 bg-navy-900 rounded-full flex items-center justify-center mb-6">
            <BuildingOfficeIcon className="h-10 w-10 text-copper-300" />
          </div>
          <h2 className="text-3xl font-bold text-navy-900 mb-2">تسجيل دخول</h2>
          <p className="text-lg text-navy-500/80">نظام إدارة الحج والعمرة</p>
        </div>

        {/* Vendor ID Input */}
        {!selectedVendor && (
          <div className="bg-white rounded-2xl shadow-lg ring-1 ring-black/5 p-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-navy-900 mb-2">أدخل الرقم الخاص بك</h3>
              <p className="text-sm text-navy-500/80">أدخل الرقم الخاص بك للبدء</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleVendorIdSubmit} className="space-y-4">
              <div>
                <label htmlFor="vendorId" className="block text-sm font-medium text-navy-700 mb-2">
                  الرقم الخاص بك
                </label>
                <input
                  id="vendorId"
                  name="vendorId"
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500 transition-colors"
                  placeholder="مثال: 1234567890"
                  value={formData.vendorId}
                  onChange={handleChange}
                  disabled={isLoadingVendor}
                />
              </div>

              <button
                type="submit"
                disabled={isLoadingVendor}
                className="w-full bg-navy-900 hover:bg-copper-500 hover:text-navy-900 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingVendor ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                    جاري التحقق...
                  </div>
                ) : (
                  'متابعة'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-navy-500/80 mb-2">ليس لديك حساب؟</p>
              <button
                type="button"
                onClick={() => navigate('/vendor-register')}
                className="text-sm text-copper-600 hover:text-copper-700 font-medium"
              >
                تسجيل مستخدم جديد
              </button>
            </div>
          </div>
        )}

        {/* Selected Vendor Display */}
        {selectedVendor && (
          <div className="bg-white rounded-2xl shadow-lg ring-1 ring-black/5 p-6">
            <div className="text-center mb-6">
              <div className="mt-3 p-4 bg-parchment-50 rounded-lg border border-copper-200">
                <div className="font-semibold text-navy-900 text-lg">{selectedVendor.companyName}</div>
                <div className="text-sm text-navy-500/80 mt-1">{selectedVendor.name}</div>
                <div className="text-xs text-navy-400 mt-2">
                  {selectedVendor.contact?.email} • {selectedVendor.contact?.phone}
                </div>
                <div className="mt-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    selectedVendor.subscription?.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedVendor.subscription?.isActive ? 'اشتراك نشط' : 'اشتراك منتهي'}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-copper-100 text-copper-700 mr-2">
                    خطة {selectedVendor.subscription?.plan || 'غير محدد'}
                  </span>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* Login form */}
        {selectedVendor && (
          <div className="bg-white rounded-2xl shadow-lg ring-1 ring-black/5 p-6">
            <form className="space-y-6" onSubmit={handleLogin}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-navy-700 mb-2">
                  اسم المستخدم
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500 transition-colors"
                  placeholder="أدخل اسم المستخدم"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-navy-700 mb-2">
                  كلمة المرور
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500 transition-colors"
                    placeholder="أدخل كلمة المرور"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-navy-400 hover:text-navy-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-navy-900 hover:bg-copper-500 hover:text-navy-900 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                      جاري تسجيل الدخول...
                    </div>
                  ) : (
                    'تسجيل الدخول'
                  )}
                </button>
              </div>
            </form>


          </div>
        )}
      </div>
    </div>
  );
};

export default VendorLogin;